import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  CircularProgress,
} from '@mui/material';
import {
  TrendingUp,
  Assignment,
  People,
  CheckCircle,
  Pending,
  Cancel,
  Refresh,
  AllInclusive,
} from '@mui/icons-material';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend, 
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell 
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { applicationService } from '../../services/applicationService';

const StatCard = ({ title, value, icon, color, loading }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography color="textSecondary" sx={{ fontSize: 10, fontWeight: 'bold' }} gutterBottom variant="overline">
          {title}
        </Typography>
      </Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h4" component="div" color={color}>
            {loading ? <CircularProgress size={20} /> : value}
          </Typography>
        </Box>
        <Box sx={{ color: color }}>
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const Reports = () => {

  const [timeRange, setTimeRange] = useState('30'); // days
  
  // Fetch dashboard statistics
  const { data: stats, isLoading: statsLoading, refetch: refetchStats } = useQuery({
    queryKey: ['dashboard-stats', timeRange],
    queryFn: () => applicationService.getApplicationStats({ days: timeRange }),
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });
    // Mock data for demonstration
  const applicationsByStatus = [
    { name: 'Approved', value: 120, color: '#4caf50' },
    { name: 'Pending', value: 45, color: '#ff9800' },
    { name: 'Rejected', value: 15, color: '#f44336' },
    { name: 'Under Review', value: 30, color: '#2196f3' },
  ];

  const monthlyApplications = [
    { month: 'Jan', applications: 0 },
    { month: 'Feb', applications: 0 },
    { month: 'Mar', applications: 0 },
    { month: 'Apr', applications: 0 },
    { month: 'May', applications: 0 },
    { month: 'Jun', applications: 0 },
    { month: 'Jul', applications: 0 },
    { month: 'Aug', applications: 0 },
    { month: 'Sep', applications: 0 },
    { month: 'Oct', applications: 0 },
    { month: 'Nov', applications: 0 },
    { month: 'Dec', applications: 0 },
  ];
  
  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Reports & Analytics | { timeRange === '7' ? 'Last 7 Days' : timeRange === '30' ? 'Last 30 Days' : 'All Time'}
      </Typography>
      
      <Typography variant="body1" color="text.secondary" mb={3}>
        View comprehensive reports and analytics for ex-gratia applications
      </Typography>

      {/* Statistics Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Applications"
            value={stats?.total || 0}
            icon={<Assignment />}
            color="primary.main"
            loading={statsLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Approved This Month"
            value={stats?.approved || 0}
            icon={<CheckCircle />}
            color="success.main"
            loading={statsLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Pending Review"
            value={stats?.pending || 0}
            icon={<Pending />}
            color="warning.main"
            loading={statsLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Rejected"
            value={stats?.rejected || 0}
            icon={<Cancel />}
            color="error.main"
            loading={statsLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Avg. Processing Time"
            value={(stats?.avg_processing_time ? stats?.avg_processing_time + " days" : "-")}
            icon={<AllInclusive />}
            color="info.main"
            loading={statsLoading}
          />
        </Grid>
      </Grid>

      <Grid container mt={3} spacing={3}>                
        {/* Charts */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 3, height: 400 }}>
            <Typography variant="h6" gutterBottom>
              Monthly Application Trends
            </Typography>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyApplications}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <RechartsTooltip />
                <Legend />
                <Bar dataKey="applications" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 3, height: 400 }}>
            <Typography variant="h6" gutterBottom>
              Application Status Distribution
            </Typography>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={applicationsByStatus}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {applicationsByStatus.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <RechartsTooltip />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      <Grid container justifyContent="flex-end" mt={3}>
        <Typography variant="caption" color="textSecondary">
          ExMS | Reports as on: {'(Today)'} {new Date().toLocaleDateString()}
        </Typography>
      </Grid>
    </Box>
  );
};

export default Reports;
